import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Users, Target } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { teamMembers, stats } from '../mockData';

export const About = () => {
  return (
    <div className="about-page pt-20">
      {/* Hero Section */}
      <section className="about-hero py-24 bg-gradient-to-br from-black via-gray-900 to-black relative overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-30" />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-6xl font-bold text-white mb-6">
              We Connect What Matters:<br />
              <span className="text-gradient">Strategy to Execution</span>
            </h1>
            <p className="text-xl text-gray-400 leading-relaxed">
              RADIUS was founded on a simple belief: marketing should not decorate a business strategy. It should express it.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="our-story py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-stretch">
            <div>
              <div className="inline-block px-4 py-2 bg-red-600/10 border border-red-600/20 rounded-full mb-6">
                <span className="text-red-500 font-medium text-sm">Our Story</span>
              </div>
              <h2 className="text-5xl font-bold text-white mb-6">
                Born from Experience,<br />Driven by Results
              </h2>
              <div className="space-y-4 text-gray-400 text-lg leading-relaxed">
                <p>
                  RADIUS Consulting was founded by strategists and creatives who have been where you have been, expectation to deliver results and having to figure it out yourself. Thats why RADIUS exists. To help you solve these problems side by side. We have worked on both sides of the table as client-side marketers and agency partners. Our strategies have never lived in decks. We have built them, run them and been accountable for what they produced across industries. We know the pressure of delivering measurable growth. We have been inside businesses, owning the strategy and delivering the work.
                </p>
                <p>
                  That is why we built RADIUS differently. We connect the dots between insight, strategy and execution. We deliver practical solutions in motion, and every engagement produces more than a campaign or a brand refresh. We build frameworks your business can follow, scale with and own long after the work is done.
                </p>
                <p>
                  That includes how we approach AI. We integrate AI execution into everything we build, but we walk with you on that journey because AI should not feel like an abyss of unknown tools and uncertain outcomes. We guide you through exactly what your business needs, what it does not, and how to use it to deliver real results. No overwhelm. No guesswork. Just the right tools, applied the right way.
                </p>
                <p>
                  Today we partner with ambitious businesses across industries, helping them express their strategy through integrated brand marketing and intelligent systems that drive measurable, sustainable growth.
                </p>
              </div>
            </div>
            <div className="relative h-full flex items-stretch">
              <img 
                src="https://customer-assets.emergentagent.com/job_marketing-hub-431/artifacts/j34u2y3d_partnership%20with%20radius%20consulting.png" 
                alt="Partnership with RADIUS - Brand strategy frameworks"
                className="w-full h-full object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-8 -left-8 w-64 h-64 bg-red-600/10 rounded-full blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="stats-section py-20 bg-black border-y border-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="stat-card text-center group">
                <div className="text-5xl font-bold text-gradient mb-2">{stat.value}</div>
                <div className="text-gray-400 text-sm uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEW SECTION: How Clients Move Through Radius */}
      <section className="growth-path py-24 bg-[#161a21] border-t border-red-500/30 relative">
        <div className="max-w-7xl mx-auto px-6">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-red-600/10 border border-red-600/20 rounded-full mb-6">
              <span className="text-red-500 font-medium text-sm font-mono uppercase tracking-wider">The Growth Path</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              How Clients Move Through Radius
            </h2>
            <p className="text-lg sm:text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              Three distinct engagements. One direction: forward. Whether you're starting from scratch or ready to scale, there's a clear entry point for where your business is right now.
            </p>
          </div>

          {/* Three Tier Cards */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            
            {/* Card 1 - Launch */}
            <Card className="tier-card group bg-[#0a0a0a] border-gray-800 hover:border-red-500/50 transition-all duration-300">
              <CardContent className="p-8">
                <div className="mb-6">
                  <div className="text-gray-500 text-xs font-mono uppercase tracking-wider mb-4">
                    01 / Engagement
                  </div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <h3 className="text-3xl font-bold text-white">Launch</h3>
                  </div>
                  <div className="text-red-400 text-sm mb-6 flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>Brand identity, website & GTM plan</span>
                  </div>
                </div>
                
                <p className="text-gray-400 leading-relaxed mb-8">
                  We build your foundations — brand identity, digital presence and a go-to-market plan that sets you up to compete from day one. You leave credible, positioned, and live in market.
                </p>
                
                <div className="pt-6 border-t border-gray-800">
                  <div className="text-gray-500 text-xs uppercase tracking-wider mb-2">Client outcome</div>
                  <div className="text-white font-semibold">Credible & live in market</div>
                </div>
              </CardContent>
            </Card>

            {/* Card 2 - Scale */}
            <Card className="tier-card group bg-[#0a0a0a] border-gray-800 hover:border-red-500/50 transition-all duration-300">
              <CardContent className="p-8">
                <div className="mb-6">
                  <div className="text-gray-500 text-xs font-mono uppercase tracking-wider mb-4">
                    02 / Engagement
                  </div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <h3 className="text-3xl font-bold text-white">Scale</h3>
                  </div>
                  <div className="text-red-400 text-sm mb-6 flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>Campaigns, creative & performance marketing</span>
                  </div>
                </div>
                
                <p className="text-gray-400 leading-relaxed mb-8">
                  We run the engine — integrated campaigns, creative execution and performance marketing that build awareness and drive revenue. Your brand starts doing real commercial work.
                </p>
                
                <div className="pt-6 border-t border-gray-800">
                  <div className="text-gray-500 text-xs uppercase tracking-wider mb-2">Client outcome</div>
                  <div className="text-white font-semibold">Actively growing</div>
                </div>
              </CardContent>
            </Card>

            {/* Card 3 - Intelligence */}
            <Card className="tier-card group bg-[#0a0a0a] border-gray-800 hover:border-red-500/50 transition-all duration-300">
              <CardContent className="p-8">
                <div className="mb-6">
                  <div className="text-gray-500 text-xs font-mono uppercase tracking-wider mb-4">
                    03 / Engagement
                  </div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <h3 className="text-3xl font-bold text-white">Intelligence</h3>
                  </div>
                  <div className="text-red-400 text-sm mb-6 flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>AI-powered content, workflows & deep strategy</span>
                  </div>
                </div>
                
                <p className="text-gray-400 leading-relaxed mb-8">
                  We integrate AI-driven systems into your marketing — content at scale, automated workflows and advanced performance intelligence. Your growth compounds instead of plateauing.
                </p>
                
                <div className="pt-6 border-t border-gray-800">
                  <div className="text-gray-500 text-xs uppercase tracking-wider mb-2">Client outcome</div>
                  <div className="text-white font-semibold">Compounding growth</div>
                </div>
              </CardContent>
            </Card>

          </div>

          {/* Callout Note */}
          <div className="max-w-[680px] mx-auto">
            <Card className="bg-[#0f1117] border-gray-800/50">
              <CardContent className="p-8 text-center">
                <p className="text-gray-400 leading-relaxed">
                  <strong className="text-white">Each tier is also a standalone entry point.</strong> Not every client starts at Launch. If your brand foundations are solid, you enter at Scale. If your marketing engine is running, you enter at Intelligence. We meet you where you are.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="values-section py-24 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-6">
              What We Believe
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Our values guide every decision, every strategy, and every creative execution.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="value-card hover-lift">
              <CardContent className="p-10">
                <div className="mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-red-600/20 to-red-700/20 rounded-2xl flex items-center justify-center">
                    <Target className="w-8 h-8 text-red-500" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Strategy First</h3>
                <p className="text-gray-400 leading-relaxed">
                  Beautiful creative without strategy is just decoration. We start with insight and build from there, ensuring every decision ladders to your business goals.
                </p>
              </CardContent>
            </Card>

            <Card className="value-card hover-lift">
              <CardContent className="p-10">
                <div className="mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-red-600/20 to-red-700/20 rounded-2xl flex items-center justify-center">
                    <Award className="w-8 h-8 text-red-500" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Accountable Excellence</h3>
                <p className="text-gray-400 leading-relaxed">
                  We're not just responsible for creative output—we're accountable for outcomes. Every metric, every KPI, every result is our responsibility.
                </p>
              </CardContent>
            </Card>

            <Card className="value-card hover-lift">
              <CardContent className="p-10">
                <div className="mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-red-600/20 to-red-700/20 rounded-2xl flex items-center justify-center">
                    <Users className="w-8 h-8 text-red-500" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">True Partnership</h3>
                <p className="text-gray-400 leading-relaxed">
                  We work with you, not around you. Your goals become our goals. Your challenges become our challenges. We're in this together.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="team-section py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-6">
              Leadership Team
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Decades of combined experience from both client-side and agency worlds.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {teamMembers.map((member, index) => (
              <Card key={index} className="team-card hover-lift">
                <CardContent className="p-8">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-48 h-48 rounded-full overflow-hidden mb-6 ring-4 ring-red-600/20">
                      <img 
                        src={member.image} 
                        alt={member.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">{member.name}</h3>
                    <div className="text-red-500 font-medium mb-4">{member.role}</div>
                    <p className="text-gray-400 leading-relaxed">{member.bio}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Culture Section */}
      <section className="culture py-24 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-5xl font-bold text-white mb-6">
                Our Culture
              </h2>
              <div className="space-y-4 text-gray-400 text-lg leading-relaxed">
                <p>
                  At RADIUS, we believe great work comes from great teams. We've built a culture where curiosity is rewarded, collaboration is the norm, and everyone's voice matters.
                </p>
                <p>
                  We're direct and transparent. We celebrate wins and learn from failures. We push each other to be better while supporting each other through challenges.
                </p>
                <p>
                  Whether you're a strategist, creative, or technologist, you'll find a home at RADIUS where your expertise is valued and your growth is prioritized.
                </p>
              </div>
            </div>
            <div>
              <img 
                src="https://images.pexels.com/photos/8117420/pexels-photo-8117420.jpeg" 
                alt="Team collaboration"
                className="w-full rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Working with Us */}
      <section className="working-with-us py-24 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-6">
              What It's Like to Work With Us
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card className="working-card">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-white mb-4">Direct & Transparent</h3>
                <p className="text-gray-400">
                  No jargon. No BS. Just clear communication about what's working, what's not, and what we're doing about it.
                </p>
              </CardContent>
            </Card>

            <Card className="working-card">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-white mb-4">Data-Informed, Not Data-Paralyzed</h3>
                <p className="text-gray-400">
                  We let data guide us but don't let it paralyze us. Smart risks informed by insight drive breakthrough results.
                </p>
              </CardContent>
            </Card>

            <Card className="working-card">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-white mb-4">Collaborative Process</h3>
                <p className="text-gray-400">
                  Your team's insight is invaluable. We work together, leveraging your internal knowledge with our external perspective.
                </p>
              </CardContent>
            </Card>

            <Card className="working-card">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-white mb-4">Always Improving</h3>
                <p className="text-gray-400">
                  We continuously test, learn, and optimize. What worked yesterday might not work tomorrow—we stay agile.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
